import { DataSetSubmissionDetailRoutePath } from "#routes/data-tools/submissions/datasets/[transactionId]/detail";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useQueueUnique } from "#src/hooks/useQueue";
import { DataSubmissionsRoutePath } from "#src/routes/data-tools/submissions";
import { useQuery } from "@tanstack/react-query";
import {
  Banner,
  IconLoading,
  type BannerProps,
} from "@validereinc/common-components";
import { TransactionAdapter, TransactionType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import styles from "./ImportDataActionStatus.module.scss";
import { Transactions } from "@validereinc/domain-controllers";

const { getStateDisplayLabel, getStepDisplayLabel } = Transactions.view;

const cx = classNames.bind(styles);
const MaxRetries = 3;

/**
 * Represents the status of a queued import data action
 */
export const ImportDataActionStatus = ({
  transactionId,
  onDismiss,
}: ImportDataActionStatusProps) => {
  const {
    data: transactionData,
    status: transactionDataStatus,
    failureCount: transactionDataFailureCount,
  } = useQuery(
    ["transactions", transactionId],
    () =>
      TransactionAdapter.getOne({
        id: transactionId,
        meta: { history: false },
      }),
    {
      enabled: Boolean(transactionId),
      refetchInterval: (data) => {
        if (!data) {
          return false;
        }

        if (
          data.data?.[0].status === "completed" ||
          data.data?.[0].status === "failed"
        ) {
          return false;
        }

        return 3000;
      },
      refetchIntervalInBackground: true,
      retry: MaxRetries,
      retryDelay: (attempt) => attempt * 1000,
    }
  );

  const mapTransactionStatusToBannerVariant = (
    status?: TransactionType["status"]
  ): BannerProps["variant"] => {
    switch (status) {
      case "completed":
      case "completed_with_partial_success":
        return "success";
      case "failed":
        return "error";
      case "pending":
        return "info";
      default:
        return "generic";
    }
  };

  const mapTransactionToStatusDisplayText = (transaction?: TransactionType) => {
    if (!transaction) {
      return "Importing Data: in-progress...";
    }

    if (transaction.status !== "pending") {
      return transaction.status === "completed" ||
        transaction.status === "completed_with_partial_success"
        ? "Successfully imported data."
        : "Failed to import data.";
    }

    const getDisplayTextForTransactionStep = (
      step: TransactionType["step"]
    ) => {
      const stepLabel = getStepDisplayLabel(step);

      switch (step) {
        case "archiver":
        case "operator":
        case "sink":
          return `Importing, ${stepLabel}`;
        case "transaction_recorder":
        case "vulnerability_scanner":
        default:
          return "Importing Data";
      }
    };

    return `${getDisplayTextForTransactionStep(
      transaction.step
    )}: ${getStateDisplayLabel(transaction.state).toLowerCase()}`;
  };

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss(transactionId);
    }
  };

  const withinRetries = transactionDataFailureCount < MaxRetries;
  // is this component still loading?
  const isLoading = withinRetries && transactionDataStatus === "loading";
  // this component doesn't have everything it needs
  const isError =
    (!withinRetries && transactionDataStatus === "error") ||
    transactionDataStatus === "error";
  // the transaction representing the import data action
  const thisTransaction = transactionData?.data?.[0];
  // is the data importing in-progress?
  const isImporting = thisTransaction && thisTransaction.status === "pending";

  if (isLoading || isError || !thisTransaction) {
    return (
      <Banner
        className={cx("banner")}
        variant={isLoading ? "generic" : "warning"}
        titleText={
          isLoading
            ? "Fetching data import details..."
            : "Couldn't fetch data import details."
        }
        icon={isLoading ? <IconLoading speed="slow" /> : undefined}
        isDismissable={!isLoading}
        onDismiss={handleDismiss}
      />
    );
  }

  return (
    <Banner
      className={cx("banner")}
      variant={mapTransactionStatusToBannerVariant(thisTransaction?.status)}
      titleText={mapTransactionToStatusDisplayText(thisTransaction)}
      icon={isImporting ? <IconLoading speed="fast" /> : undefined}
      actionContent={
        <>
          {isImporting ? (
            <>You can also see the upload status in the </>
          ) : (
            <>You can also see detailed submission information in the </>
          )}
          <RoutingLink
            variant="secondary"
            underline="always"
            to={
              thisTransaction?.transaction_id
                ? DataSetSubmissionDetailRoutePath.toLinkParts({
                    pathParams: {
                      transactionId: thisTransaction.transaction_id,
                    },
                  })
                : DataSubmissionsRoutePath.toLinkParts({
                    queryParams: { tab: "datasets" },
                  })
            }
          >
            Submission Details
          </RoutingLink>
        </>
      }
      isDismissable
      onDismiss={handleDismiss}
    />
  );
};

/**
 * Renders the ImportDataActionStatus BIC in-sync with a queue in local storage representing queued data import actions
 */
export const ImportDataActionStatusWithQueue = ({
  queueKey,
}: ImportDataActionStatusWithQueueProps) => {
  const [queue, setQueue] = useQueueUnique<TransactionType>(
    [],
    queueKey,
    (tx) => tx.transaction_id
  );

  const handleDismiss = (transactionId: string) => {
    setTimeout(() => {
      setQueue(queue.cutById(transactionId));
    }, 500);
  };

  return (
    <>
      {queue.size()
        ? queue.getAsArray().map((item) => (
            <ImportDataActionStatus
              key={item.transaction_id}
              transactionId={item.transaction_id}
              onDismiss={handleDismiss}
            />
          ))
        : null}
    </>
  );
};

export type ImportDataActionStatusProps = {
  transactionId: string;
  onDismiss?: (transactionId: string) => void;
};

export type ImportDataActionStatusWithQueueProps = {
  queueKey: string;
};
